import { Star, Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";

const reviews = [
  {
    quote: "Great insurance company to work with. Very knowledgeable, patient, and willing to answer all our questions. Very customer oriented and personable, and have positive reviews from employees who are happy we added them to our benefit packages.",
    name: "Heather M",
    role: "HR Director",
    stars: 5,
  },
  {
    quote: "They're super easy to work with and offer very affordable policies.",
    name: "Steven Burckhard",
    role: "Business Owner",
    stars: 5,
  },
  {
    quote: "Working with Joey and Levi has been a great experience. They return phone calls, answer emails, and are always willing to make the drive to help fill out claims or help get our Colonial Portal set up. It's wonderful to have a representative that is willing to go the distance to keep our employees happy. Would highly recommend.",
    name: "Colleen Severinson",
    role: "HR Director",
    stars: 5,
  },
  {
    quote: "Great price! Trevor and Jason are awesome to work with, and we appreciate them offering these benefits to our company!",
    name: "Jan Jaeger",
    role: "HR Director",
    stars: 5,
  },
  {
    quote: "Levi is quick to respond and knowledgeable! I definitely recommend!",
    name: "Amanda McCallum",
    role: "Business Owner",
    stars: 5,
  },
];

const ReviewCard = ({ review }) => (
  <div
    data-testid={`review-card-${review.name}`}
    className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex space-x-0.5">
        {[1, 2, 3, 4, 5].map((j) => (
          <Star key={j} size={16} className={j <= review.stars ? "text-yellow-400" : "text-gray-200"} fill={j <= review.stars ? "#FBBF24" : "#E5E7EB"} stroke="none" />
        ))}
      </div>
      <Quote size={20} className="text-[#2EC4C4] opacity-40" />
    </div>
    <p className="text-[#475569] leading-relaxed italic mb-5">"{review.quote}"</p>
    <div className="border-t border-gray-100 pt-4">
      <p className="font-semibold text-[#1A2744]" style={{ fontFamily: "'Outfit', sans-serif" }}>{review.name}</p>
      <p className="text-sm text-[#94A3B8]">{review.role} · Google Review</p>
    </div>
  </div>
);

const HeroSection = () => (
  <section data-testid="reviews-hero" className="bg-[#1A2744] py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="flex items-center justify-center space-x-1 mb-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} size={32} className="text-yellow-400" fill="#FBBF24" stroke="none" />
        ))}
      </div>
      <h1 className="font-bold text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Client Reviews
      </h1>
      <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-6">
        Don't just take our word for it. Here's what our clients say about working with Viall Business Solutions.
      </p>
      <span className="inline-block bg-[#2EC4C4] text-white font-bold text-xl px-8 py-3 rounded-full">
        5.0 Stars on Google
      </span>
    </div>
  </section>
);

const ReviewsGrid = () => (
  <section data-testid="reviews-grid" className="py-20 bg-[#F8FAFC]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.map((review) => <ReviewCard key={review.name} review={review} />)}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section data-testid="reviews-cta" className="py-16 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="font-bold text-3xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Ready to Join Our Happy Clients?
      </h2>
      <p className="text-[#475569] text-lg mb-8">
        Let us show you why North Dakota businesses trust Viall Business Solutions.
      </p>
      <Link
        to="/contact"
        data-testid="reviews-cta-btn"
        className="inline-flex items-center space-x-2 bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-8 py-4 text-base transition-all shadow-md hover:shadow-lg active:scale-95"
      >
        <span>Get Your Free Quote</span>
        <ArrowRight size={18} />
      </Link>
    </div>
  </section>
);

export default function ReviewsPage() {
  usePageMeta({
    title: "Client Reviews | 5-Star Rated Benefits Agency in North Dakota",
    description: "Read why North Dakota small businesses choose Viall Business Solutions. 5.0 stars on Google. Real reviews from real clients about our employee benefits services.",
    path: "/reviews",
  });
  return (
    <div className="pt-16 lg:pt-20">
      <HeroSection />
      <ReviewsGrid />
      <CTASection />
    </div>
  );
}
